import RoleTypes from "@wesstron/utils/Api/constants/roleTypes";
import {
    cloneDeep,
    debounce,
    isBoolean,
    isEqual,
    isFunction,
    isString,
    throttle,
} from "lodash";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import React, {Component} from "react";
import {Col, Row} from "react-bootstrap";
import {withTranslation} from "react-i18next";
import {connect} from "react-redux";
import {compose} from "redux";
import {show} from "redux-modal";
import * as MatrixUtils from "transformation-matrix";
import NewIOT from "../../IOT/NewIOT";
import {changeMapSettings} from "../../actions/editFarmMapActions";
import {ModalName as SettingModal} from "../../components/modals-new/map-settings-modal/MapSettingsModal";
import LicensePaths from "../../constans/LicensePaths";
import {
    farmMapDataSelector,
    makeFarmMapDataSelector,
} from "../../selectors/farmMapDataSelector";
import {getAnimalColorFunc} from "../../selectors/farmMapSelector";
import {getGroupMapWithAnimalID} from "../../selectors/groupSelector";
import {getPigletNumber} from "../../selectors/inventorySelector";
import {getMapSettings} from "../../selectors/mapSelector";
import {
    createWheelNormalizer,
    getRemToPx,
    onElementPainted,
} from "../../utils/DOMUtils";
import {
    farmMapTransformAnimals,
    validateCustomFarmAnimals,
} from "../../utils/FarmMapHelperUtils";
import {PRE_PROCESS_SCALING} from "../../utils/FarmMapUtils";
import {rotateCartesian} from "../../utils/MathUtils";
import {isMobile} from "../../utils/MobileUtils";
import {ReactLocalStorage} from "../../utils/ReactLocalStorage";
import {catchify, getReferenced, isStringifyEqual} from "../../utils/Utils";
import {Browser} from "../basics/browser-detection/Browser";
import Button from "../basics/button/Button";
import Patterns from "../farm-map-components/patterns";
import Gradients from "../farm-map-components/patterns/gradients/Gradients";
import BackgroundSVG from "../farm-map-editor/BackgroundSVG";
import MapSettingsModal from "../modals-new/map-settings-modal/MapSettingsModal";
import Scale from "../svg-editor/utils/Scale";
import withRoles from "../withRoles";
import "./_farm-map-component.scss";
import ElementList from "./components/ElementList";
import LevelSelect from "./components/LevelSelect";
import LocationDrawer from "./components/LocationDrawer";
import SearchAnimal from "./components/SearchAnimal";
import SelectedItem from "./components/SelectedItem";
import TypeSelect from "./components/TypeSelect";
import FilterAnimalControl from "./components/animal-controls/FilterAnimalControl";
import {makeAnimations} from "./components/animations";
import FarmMapControlContainer from "./components/farm-map-controls/FarmMapControlContainer";
import Rotate from "./components/farm-map-controls/Rotate";
import Zoom from "./components/farm-map-controls/Zoom";
import GlobalControlPanel from "./components/global-controls/GlobalControlPanel";
import NativeEventHandler from "./gesture-handlers/NativeEvent.handler";
import TwoFingerHandler from "./gesture-handlers/TwoFinger.handler";
import {
    onBeforeRenderAll,
    onBeforeRenderAnimalFilter,
    onBeforeRenderClimates,
    onBeforeRenderElectrics,
    onBeforeRenderLights,
    onBeforeRenderWater,
} from "./utils";

function makeMapStateToProps() {
    const emptyObj = {};
    const emptyMap = new Map();
    const emptyArray = [];

    const getDataSelector = memoizeOne((isCustom) =>
        isCustom ? makeFarmMapDataSelector() : farmMapDataSelector
    );

    const hasCustomAnimals = memoizeOne(validateCustomFarmAnimals);
    const transformAnimals = memoizeOne(farmMapTransformAnimals);

    return function mapStateToProps(state, props) {
        const overrideValues = {};
        const mapSettings = getMapSettings(state);

        let isCustom = hasCustomAnimals(
            props.animals,
            props.allowGroupsInsideCustomAnimalList,
            props.magicAnimalsFromPlacement
        );

        overrideValues.animals = isCustom ? props.animals : null;

        if (props.forceCustomAnimals && !isCustom) {
            overrideValues.animals = emptyArray;
            isCustom = true;
        }

        let _originalAnimals = overrideValues.animals;

        overrideValues.animals = transformAnimals(
            overrideValues.animals,
            props
        );

        return {
            farmData: getDataSelector(isCustom)(state, overrideValues),
            getAnimalColor: getAnimalColorFunc(state),
            mapSettings,
            groupMap: isCustom ? emptyMap : getGroupMapWithAnimalID(state),
            balanceMap:
                mapSettings?.numberOfPiglets && !isCustom
                    ? getPigletNumber(state)
                    : emptyObj,
            isUsingCustomAnimals: isCustom,
            _originalAnimals,
        };
    };
}

class FarmMapComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentLevel: props.farmData?.levels?.[0] ?? 0,
            isMoving: false,
            mapType: "chambers",
            selectedId: null,
            camera: {x: 0, y: 0, zoom: 1, rotate: 0},
            __selectionTime__: 0,
        };
        this.portal = React.createRef();
        this.cache = {};
        this.mobile = isMobile();
        this.scaleOptions = new Scale();
        if (this.mobile) {
            this.scaleOptions.makeSafe();
        }
        this.localCamera = cloneDeep(this.state.camera);
        this.isChrome = Browser.is.Chrome();
        this.transitionType = "auto";
        NewIOT.resetCounters();
        this.animations = makeAnimations(this.setAnimationState);
    }

    setAnimationState = (func) => {
        this.setState(func);
    };

    setLevel = (level) => {
        this.setState({
            currentLevel: level,
        });
    };
    setSelectedId = (value) => {
        this.setState((state) => ({
            selectedId: isString(value) ? value : value?.id || null,
            selectedType: isString(value)
                ? "location"
                : ["devices", "groups"].includes(value?.type)
                  ? "device"
                  : value?.type === "animals"
                    ? "animal"
                    : "location",
            animate: isString(value),
            animateType: isString(value) ? "search" : "click",
            mapType:
                isString(value) && !["chambers", "all"].includes(state.mapType)
                    ? "chambers"
                    : state.mapType,
            __selectionTime__: Date.now(),
        }));
    };

    adjustLevel = memoizeOne((levels) => {
        this.setLevel(levels?.[0] ?? 0);
    }, isStringifyEqual);

    openModal = (value) => {
        this.setState({
            selectedId: value?.location?.id || null,
            animateType: "longclick",
        });
    };
    openSettingsModal = () => {
        const {dispatch} = this.props;
        dispatch(show(SettingModal));
    };
    setMapType = (mapType) => {
        this.setState({
            mapType: mapType,
        });
    };

    onBeforeRenderAnimals = (getAnimalColor) =>
        memoizeOne((params) => {
            return onBeforeRenderAnimalFilter(params, getAnimalColor);
        });
    onBeforeRenderAll = memoizeOne((mapSettings, groupMap, balanceMap) => {
        return (args) =>
            onBeforeRenderAll(args, {mapSettings, groupMap, balanceMap});
    });
    getOnBeforeRender = () => {
        const {
            onBeforeRender,
            getAnimalColor,
            mapSettings,
            groupMap,
            balanceMap,
        } = this.props;
        const {mapType} = this.state;
        if (isFunction(onBeforeRender)) return onBeforeRender;
        switch (mapType) {
            case "climates":
                return onBeforeRenderClimates;
            case "electric":
                return onBeforeRenderElectrics;
            case "water":
                return onBeforeRenderWater;
            case "lights":
                return onBeforeRenderLights;
            case "animals":
                return this.onBeforeRenderAnimals(getAnimalColor);
            default:
                return this.onBeforeRenderAll(
                    mapSettings,
                    groupMap,
                    balanceMap
                );
        }
    };

    getOmitNames = () => {
        const {onBeforeRender} = this.props;
        const {mapType} = this.state;
        if (isFunction(onBeforeRender)) return [];
        switch (mapType) {
            case "lights":
            case "climates":
            case "electric":
            case "water":
                return getReferenced([
                    "buildings",
                    "sectors",
                    "standings",
                    "groups",
                    "animals",
                ]);
            default:
                return getReferenced(["buildings", "sectors"]);
        }
    };

    firstZoom = () => {
        const {
            farmData: {levelBounds},
            selectedId,
        } = this.props;
        if (levelBounds && levelBounds[0]) {
            const {x1, x2, y1, y2} = levelBounds[0];
            this.transitionType = "INSTANT";
            this.setViewportAt(x1, y1, x2, y2);
        }
        if (selectedId) {
            this.transitionType = "DELAYED_SMOOTH_ZOOM";
            this.setSelectedId(selectedId);
        }
    };

    initializeMapSettings() {
        const {mapSettings, dispatch} = this.props;
        const localMapSettings = ReactLocalStorage.getPrivate("mapSettings");
        if (localMapSettings && !isEqual(localMapSettings, mapSettings)) {
            dispatch(changeMapSettings(localMapSettings));
        }
    }

    componentDidMount() {
        const {alternateGestures, disableRotate} = this.props;
        const element = this.portal.current;
        if (this.mobile) {
            const GestureHandler = alternateGestures
                ? TwoFingerHandler
                : NativeEventHandler;
            this.gestureHandler = new GestureHandler(
                this.portal,
                this.updateCamera,
                this.scaleOptions,
                () => this.localCamera,
                {disableRotate}
            );
        } else {
            element.addEventListener("wheel", this.wheel, {passive: false});
            element.addEventListener("mousedown", this.pointerDown);
            document.addEventListener("mouseup", this.pointerUp);
            document.addEventListener("mousemove", this.pointerMove);
        }
        window.addEventListener("resize", this.onResize);
        this.initializeMapSettings();
        onElementPainted(this.firstZoom);
        this.adjustLevel(this.props.farmData?.levels);
    }

    onResize = throttle(() => {
        this.cache = {};
        this.forceUpdate();
    }, 125);

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {camera} = this.state;
        if (camera !== prevState.camera && !this.move) {
            this.saveCamera.cancel();
            this.localCamera = cloneDeep(camera);
        }
        this.adjustLevel(this.props.farmData?.levels);
        if (!isEqual(prevState.selection, this.props.selection)) {
            this.handleExternalSelectionChange();
        }
    }

    handleExternalSelectionChange = () => {
        const {
            selection,
            resolveIdFromSelection,
            farmData: {data},
        } = this.props;
        const {currentLevel, selectedId} = this.state;
        const response = resolveIdFromSelection?.({
            wholeData: data,
            selection,
            currentLevel,
        });
        if (response) {
            if (isString(response)) {
                if (selectedId !== response) {
                    this.setSelectedId(response);
                }
            } else {
                if (selectedId !== response.id) {
                    this.setSelectedId(response.id);
                }
            }
        } else {
            if (selectedId !== null) {
                this.setSelectedId(null);
            }
        }
    };

    componentWillUnmount() {
        this.animations.destroy();
        this.saveCamera.cancel();
        const element = this.portal.current;
        if (this.mobile) {
            this.gestureHandler?.destroy();
        } else {
            element.removeEventListener("wheel", this.wheel);
            element.removeEventListener("mousedown", this.pointerDown);
            document.removeEventListener("mouseup", this.pointerUp);
            document.removeEventListener("mousemove", this.pointerMove);
        }
        window.removeEventListener("resize", this.onResize);
    }

    showControls = () => {
        return !isFunction(this.props.onBeforeRender);
    };

    getItems = memoizeOne((data, level) => {
        return data[level] || [];
    });

    _realViewBox = memoizeOne((width = 0, height = 0, mat) => {
        const {applyToPoint, inverse} = MatrixUtils;
        const matInverse = inverse(mat);
        const viewPortParams = [
            applyToPoint(matInverse, [0, 0]),
            applyToPoint(matInverse, [0, height]),
            applyToPoint(matInverse, [width, 0]),
            applyToPoint(matInverse, [width, height]),
        ];
        let minX = viewPortParams[0][0],
            minY = viewPortParams[0][1],
            maxX = viewPortParams[0][0],
            maxY = viewPortParams[0][1];
        for (let i = 1; i < viewPortParams.length; i++) {
            minX = Math.min(viewPortParams[i][0], minX);
            minY = Math.min(viewPortParams[i][1], minY);
            maxX = Math.max(viewPortParams[i][0], maxX);
            maxY = Math.max(viewPortParams[i][1], maxY);
        }
        const boundsOffset = 15;
        minX -= boundsOffset;
        minY -= boundsOffset;
        maxY += boundsOffset;
        maxX += boundsOffset;
        return {
            minX,
            maxX,
            minY,
            maxY,
        };
    });

    getRealViewBox = () => {
        const width = this.getPortalWidth();
        const height = this.getPortalHeight();
        const mat = this.getMatrix();
        return this._realViewBox(width, height, mat);
    };

    showSmalls = () => {
        const {
            camera: {zoom},
            mapType,
        } = this.state;
        const {onBeforeRender, minZoomForDetails} = this.props;
        return isFunction(onBeforeRender) ||
            ["chambers", "all", "animals"].includes(mapType)
            ? zoom > (minZoomForDetails ?? 1.2) / PRE_PROCESS_SCALING
            : false;
    };

    setLocalCameraAsState = () => {
        if (this.localCamera) {
            this.setState({
                camera: cloneDeep(this.localCamera),
            });
        }
    };

    updateCamera = (fn) => {
        if (this.localCamera) {
            this.localCamera = fn(this.localCamera);
            catchify(() => {
                const matrix = this.getMatrix({camera: this.localCamera});
                const container =
                    this.portal.current.getElementsByClassName(
                        "svg-container"
                    )[0];
                container.style.transform = `${MatrixUtils.toCSS(matrix)}`;
            })();
            this.saveCamera();
        }
    };

    saveCamera = debounce(this.setLocalCameraAsState, 500);

    pointerDown = (e) => {
        const {selectedId} = this.props;
        this.move = true;
        this.moving = false;
        this.movementX = 0;
        this.movementY = 0;
        this.angle = null;
        // if the selectedId is overridden by a prop make sure no one can change it
        if (e.target.nodeName === "svg" && !selectedId) {
            this.setState({
                selectedId: null,
            });
        }
        this.forceUpdate();
    };

    setZoom = (newZoom) => {
        this.setState((state) => ({
            camera: {
                ...state.camera,
                zoom: newZoom,
            },
        }));
    };

    pointerMove = (e) => {
        if (this.move) {
            this.moving = true;
            const {movementX, movementY} = e;
            const {zoom, rotate} = this.localCamera;
            const {x, y} = rotateCartesian(movementX, movementY, -rotate);
            this.movementX += x / zoom;
            this.movementY += y / zoom;
            if (e.preventDefault) {
                e.preventDefault();
                e.stopPropagation();
            }
            const xm = this.movementX,
                ym = this.movementY;
            this.movementX = 0;
            this.movementY = 0;
            this.updateCamera((cam) => ({
                ...cam,
                x: cam.x - xm,
                y: cam.y - ym,
            }));
        }
    };

    pointerUp = () => {
        this.move = false;
        this.moving = false;
        this.angle = null;
        this.forceUpdate();
        NewIOT.getCounters(true);
    };

    setScale = (next = true, portalX = 0, portalY = 0) => {
        this.setState((state) => {
            const prevZoom = state.camera.zoom;
            const nextZoom = isBoolean(next)
                ? this.scaleOptions
                      .setCurrent(prevZoom)
                      [next ? "next" : "prev"]().scale
                : this.scaleOptions.setCurrent(next * prevZoom).scale;
            // todo: dorobic tutaj translate zeby nie zoomowalo do srodka zawsze na dekstopie?
            return {
                camera: {...state.camera, zoom: nextZoom},
                animate: false,
            };
        });
    };

    setViewportAt = (x1, y1, x2, y2) => {
        if (!this.portal.current) return;
        this.animations.stop();
        const portalWidth = this.getPortalWidth(),
            portalHeight = this.getPortalHeight();
        const width = x2 - x1,
            height = y2 - y1;
        const scaleW = width / portalWidth,
            scaleH = height / portalHeight;
        const isSmall = width < 40 || height < 40;
        const zoom = this.scaleOptions.setCurrent(
            (this.mobile ? 0.7 : isSmall ? 0.2 : 0.8) / Math.max(scaleH, scaleW)
        ).scale;
        const x = x1 + width / 2,
            y = y1 + height / 2;
        const {animateType} = this.state;
        let transitionType = this.transitionType;
        this.transitionType = "auto";
        if (transitionType === "auto") {
            transitionType = "NORMAL";
            if (animateType === "search") {
                transitionType = "ZOOM_AND_MOVE";
            }
        }
        this.animations.run(transitionType, {x, y, zoom});
    };

    wheel = createWheelNormalizer(
        ({originalEvent, direction, isTouchpad, thresholdReached, reset}) => {
            if (this.move || !this.portal.current) return;

            originalEvent.preventDefault();

            if (isTouchpad && !thresholdReached) return;

            const next = direction > 0;
            const prevZoom = this.localCamera.zoom;

            this.zoom = this.scaleOptions
                .setCurrent(prevZoom)
                [next ? "next" : "prev"]().scale;

            const {x: prevX, y: prevY} = this.localCamera;
            const nextZoom = this.zoom;

            const factor = prevZoom / nextZoom;
            const {x, y} = this.screenToWorldPoint(
                originalEvent.clientX,
                originalEvent.clientY
            );

            const newX = prevX * factor + (1 - factor) * x;
            const newY = prevY * factor + (1 - factor) * y;

            this.updateCamera((camera) => ({
                ...camera,
                zoom: nextZoom,
                x: newX,
                y: newY,
            }));

            if (isTouchpad) {
                reset();
            }
        }
    );

    getPortalHeight = () => {
        const {portalHeight} = this.props;
        if (!this.cache["height"])
            this.cache["height"] =
                portalHeight ||
                this.portal.current?.getBoundingClientRect().height ||
                0;
        return this.cache["height"];
    };

    getPortalWidth = () => {
        const {portalWidth} = this.props;
        if (!this.cache["width"])
            this.cache["width"] =
                portalWidth ||
                this.portal.current?.getBoundingClientRect().width ||
                0;
        return this.cache["width"];
    };

    getThrottledBoundingClientRect() {
        if (
            !this.cache.getBoundingClientRect ||
            !this.cache.getBoundingClientRect.timestamp
        ) {
            this.cache.getBoundingClientRect = {
                value: this.portal.current?.getBoundingClientRect(),
                timestamp: performance.now(),
            };
        } else {
            const now = performance.now();
            const throttleMs = 500;

            if (now - this.cache.getBoundingClientRect.timestamp > throttleMs) {
                this.cache.getBoundingClientRect = {
                    value: this.portal.current?.getBoundingClientRect(),
                    timestamp: now,
                };
            }
        }
        return this.cache.getBoundingClientRect.value;
    }

    /**
     * gets point on the map which corresponds to passed screen point
     * @param pointX
     * @param pointY
     * @return {{x: number, y: number}|{x: *, y: *}}
     */
    screenToWorldPoint = (pointX, pointY) => {
        if (this.portal.current) {
            // 1. get inverse matrix
            const {applyToPoint, inverse} = MatrixUtils;
            const inverseMat = inverse(
                this.getMatrix({camera: this.localCamera})
            );
            // 2. get viewBox size and cache it
            const viewBox = this.getThrottledBoundingClientRect();
            // 3. convert screen point to viewBox point
            // 4. apply inverse matrix to viewBox point
            const [newX, newY] = applyToPoint(inverseMat, [
                pointX - viewBox.x,
                pointY - viewBox.y,
            ]);
            return {x: newX, y: newY};
        }
        return {x: 0, y: 0};
    };

    getMatrix = ({camera} = this.state) => {
        const {rotate, x, y, zoom} = camera;
        const {translate, rotateDEG, scale, compose} = MatrixUtils;
        let width = this.getPortalWidth(),
            height = this.getPortalHeight();
        // offset zeby wysrodkowac widok
        const offsetX = width / 2;
        const offsetY = height / 2;
        const px = -x + offsetX,
            py = -y + offsetY;
        // smooth matrix mial pomoc z blurami gdy mamy doczynienia z liczbami zmiennoprzecinkowymi ale jak smoothujemy to przyblizenia nie sa plynne
        return compose(
            translate(px, py),
            rotateDEG(rotate, x, y),
            scale(zoom, zoom, x, y)
        );
    };

    setRotate = (value) => {
        this.updateCamera((camera) => ({
            ...camera,
            rotate: value,
        }));
    };

    setAnimate = () => {
        this.setState({
            animate: true,
            animateType: "click",
        });
    };

    onNotFoundCallback = () => {
        this.setState({
            animate: false,
        });
    };

    render() {
        const {
            useShadow,
            useAlarm,
            useMinimalVersion,
            farmData: {
                background,
                levels,
                data,
                type,
                width: widthRaw,
                height: heightRaw,
            },
        } = this.props;
        const width = widthRaw * PRE_PROCESS_SCALING;
        const height = heightRaw * PRE_PROCESS_SCALING;
        const {
            currentLevel,
            mapType,
            selectedId,
            selectedType,
            animate,
            camera,
            animateType,
            __selectionTime__,
        } = this.state;
        const items = this.getItems(data, currentLevel);
        const {
            onElementClick,
            sizeOffset,
            isUsingCustomAnimals,
            t,
            _originalAnimals,
            disableAutoRotateLabels,
            disableRotate,
            portalHeight: hasFixedHeight,
        } = this.props;
        const showSmalls = this.showSmalls();
        const portalHeight = hasFixedHeight
            ? hasFixedHeight
            : this.portal.current
              ? window.innerHeight -
                this.portal.current.getBoundingClientRect().top -
                sizeOffset
              : Math.floor(window.innerHeight * 0.7);
        // ustawiamy cache dla wysokosci
        this.cache["height"] = portalHeight;
        const showControls = this.showControls();
        const showMessage = ["error", "empty"].includes(type);
        const classNames = ["farm-map-component"];
        if (animate) classNames.push("animate");
        if (this.move) classNames.push("moving");
        if (this.mobile) classNames.push("mobile");
        if (this.isChrome) classNames.push("chrome");
        const matrix = this.getMatrix();
        return (
            <div className={classNames.join(" ")} cy-data="farm-map">
                <canvas id="area-canvas" />
                {!showMessage && !this.mobile && (
                    <Col xs={12} className={"px-sm-0"}>
                        <Row>
                            {showControls && (
                                <>
                                    <Col
                                        className={
                                            "col-xl-auto col-lg-auto flex-fill"
                                        }
                                        xs={12}
                                        md={12}
                                        lg={6}>
                                        <SearchAnimal
                                            onSearch={this.setSelectedId}
                                        />
                                    </Col>
                                    <Col
                                        className={
                                            "col-xl-auto col-lg-auto d-flex align-items-center"
                                        }
                                        xs={12}
                                        md={12}
                                        lg={6}>
                                        <TypeSelect
                                            mapType={mapType}
                                            onSelect={this.setMapType}
                                        />
                                        <Button
                                            onClick={this.openSettingsModal}
                                            buttonColor="menu-color"
                                            cy-data="map-settings"
                                            title={t("settings.settings")}>
                                            <i className="fa-solid fa-gear" />
                                        </Button>
                                    </Col>
                                </>
                            )}
                            {levels.length > 1 && (
                                <Col
                                    className={"col-md-auto flex-fill"}
                                    xs={12}
                                    md={6}
                                    lg={4}>
                                    <LevelSelect
                                        level={currentLevel}
                                        levels={levels}
                                        onSelect={this.setLevel}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Col>
                )}
                {showControls && (
                    <>
                        <GlobalControlPanel
                            mobile={this.mobile}
                            changeOptionsCallback={this.onResize}
                            mapType={mapType}
                        />
                        {mapType === "animals" && <FilterAnimalControl />}
                    </>
                )}
                <div className={"portal-container"}>
                    <div id="response-container" />
                    {!showMessage && this.mobile && (
                        <div className={"ctrl-container"}>
                            {showControls && (
                                <>
                                    <div className={"s-box"}>
                                        {mapType !== "animals" && (
                                            <SearchAnimal
                                                mobile={this.mobile}
                                                onSearch={this.setSelectedId}
                                            />
                                        )}
                                    </div>
                                    <div className={"t-box"}>
                                        <TypeSelect
                                            mobile={this.mobile}
                                            mapType={mapType}
                                            onSelect={this.setMapType}
                                        />
                                    </div>
                                    <div>
                                        <Button
                                            onClick={this.openSettingsModal}
                                            buttonColor="menu-color"
                                            buttonStyle={"round"}
                                            cy-data="map-settings"
                                            icon={
                                                <i
                                                    className={`fa-solid fa-gear`}
                                                />
                                            }
                                            title={t("settings.settings")}
                                        />
                                    </div>
                                </>
                            )}
                            {levels.length > 1 && (
                                <div className={"l-box"}>
                                    <LevelSelect
                                        mobile={this.mobile}
                                        level={currentLevel}
                                        levels={levels}
                                        onSelect={this.setLevel}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div
                        ref={this.portal}
                        style={portalHeight ? {height: portalHeight} : null}
                        className={"view-portal"}>
                        {!useMinimalVersion && (
                            <>
                                <FarmMapControlContainer mobile={this.mobile}>
                                    {!disableRotate && (
                                        <Rotate
                                            mobile={this.mobile}
                                            rotate={camera.rotate}
                                            onRotate={this.setRotate}
                                        />
                                    )}
                                    <Zoom
                                        mobile={this.mobile}
                                        zoom={camera.zoom}
                                        scaleOptions={this.scaleOptions}
                                        onZoom={this.setZoom}
                                    />
                                </FarmMapControlContainer>
                            </>
                        )}
                        {showMessage && (
                            <div className={"map-message"}>
                                <i className={"fas me-2 fa-exclamation"} />
                                {type === "error"
                                    ? t("farms.tabs.mapTab.farmMapError")
                                    : t("farms.tabs.mapTab.farmMapNotFound")}
                            </div>
                        )}
                        {isUsingCustomAnimals && (
                            <div className={"map-message map-message__small"}>
                                <i className={"fas me-2 fa-history"} />
                                {t("mapWarningDataNotFullCoverage")}
                            </div>
                        )}
                        <div
                            className={"svg-container"}
                            style={{
                                width,
                                height,
                                transform: `${MatrixUtils.toCSS(matrix)}`,
                            }}>
                            <svg
                                width={width}
                                height={height}
                                viewBox={`0 0 ${width} ${height}`}>
                                <defs>
                                    <Patterns
                                        scale={camera.zoom || 1}
                                        enableShadow
                                        level={currentLevel}
                                    />
                                    <Gradients showSmalls={showSmalls} />
                                </defs>
                                <BackgroundSVG
                                    background={background}
                                    opacity={1}
                                    blur={0}
                                />
                                <ElementList
                                    key={mapType}
                                    omitNames={this.getOmitNames()}
                                    viewBox={this.getRealViewBox()}
                                    enableDataFetcher={
                                        !!useAlarm || !!useShadow
                                    }
                                    items={items}
                                    onClick={
                                        onElementClick || this.setSelectedId
                                    }
                                    passOrgAnimalListOnClick={
                                        !!onElementClick && isUsingCustomAnimals
                                    }
                                    originalAnimalsList={_originalAnimals}
                                    onDoubleClick={
                                        onElementClick ? null : this.setAnimate
                                    }
                                    mobile={this.mobile}
                                    rotate={
                                        disableAutoRotateLabels
                                            ? false
                                            : camera.rotate
                                    }
                                    showSmalls={showSmalls}
                                    onBeforeRender={this.getOnBeforeRender()}
                                />
                                <SelectedItem
                                    onNotFoundCallback={this.onNotFoundCallback}
                                    mobile={this.mobile}
                                    animate={animate}
                                    setLevel={this.setLevel}
                                    setPositionAt={this.setViewportAt}
                                    itemId={selectedId}
                                    modTime={__selectionTime__}
                                    currentLevel={items}
                                    allLevel={data}
                                />
                                {/*uncomment this to show initial zoom point of interest*/}
                                {/*{!!levelBounds &&*/}
                                {/*    <rect opacity={0.5} pointerEvents={"none"} x={levelBounds[0].x1}*/}
                                {/*          y={levelBounds[0].y1} width={levelBounds[0].x2 - levelBounds[0].x1}*/}
                                {/*          height={levelBounds[0].y2 - levelBounds[0].y1}></rect>*/}
                                {/*}*/}
                            </svg>
                        </div>
                    </div>
                    {showControls && (
                        <LocationDrawer
                            mapType={mapType}
                            type={selectedType}
                            mobile={this.mobile}
                            animateType={animateType}
                            selectedId={selectedId}
                        />
                    )}
                </div>
                <MapSettingsModal />
            </div>
        );
    }
}

FarmMapComponent.defaultProps = {
    useShadow: true,
    useAlarm: true,
    sizeOffset: getRemToPx(8),
    useMinimalVersion: false,
    allowGroupsInsideAnimalList: false,
};

FarmMapComponent.propTypes = {
    useShadow: PropTypes.bool,
    useAlarm: PropTypes.bool,
    onBeforeRender: PropTypes.func,
    onElementClick: PropTypes.func,
    sizeOffset: PropTypes.number,
    selectedId: PropTypes.string, // default value for selected item
    portalWidth: PropTypes.number,
    portalHeight: PropTypes.number,
    useMinimalVersion: PropTypes.bool,
    disableAutoRotateLabels: PropTypes.bool,
    disableRotate: PropTypes.bool,
    alternateGestures: PropTypes.bool, // only zoom and pan works both use 2 point touch events
    minZoomForDetails: PropTypes.number, // lower values make animal details appear at earlier (more zoomed-out) zoom levels
    animals: PropTypes.arrayOf(
        PropTypes.shape({
            AnmID: PropTypes.string.isRequired,
            PlcmntID: PropTypes.string.isRequired,
            AnmNo1: PropTypes.string.isRequired,
            AnimalKind: PropTypes.string.isRequired,
            AnmCnt: PropTypes.number,
        })
    ), // a custom animal array allows the map to render using historical animal placements
    // if you have to use one of these you are COOKED
    forceCustomAnimals: PropTypes.bool, // if true and an invalid 'animals' array is passed, no animals will be shown on the farm map
    allowGroupsInsideCustomAnimalList: PropTypes.bool, // if true, attempts to convert group references (e.g. 'AnmGrp/GroupName') to individual animals
    resolveIdFromSelection: PropTypes.func, // receives the full data set and the selection object, and should return a single ID corresponding to a map object
    selection: PropTypes.any, // external way to control the selected item; used together with `resolveIdFromSelection`
    magicAnimalsFromPlacement: PropTypes.bool, // skip providing animals — placement is enough! fantasy creatures will magically appear ✨
};

export default compose(
    withRoles({
        roles: [RoleTypes.FARM_MAP_READ],
        showInfo: true,
        requiredLicenses: {
            and: [LicensePaths.SOLIDMAP],
        },
    }),
    withTranslation(),
    connect(makeMapStateToProps)
)(FarmMapComponent);
